import React, { useEffect, useState } from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Collapse,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Gmail from "../Images/gmail.png";
import instagram from "../Images/instagram.png";
import WhatsApp from "../Images/apple.png";

import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getDrawerNavLinks, fetchShopCategories } from "./navConfig";
import { NAV } from "./navbarTheme";

const drawerPanelSx = {
  borderRadius: 2.5,
  border: `1px solid ${NAV.categorySubmenuBorder}`,
  bgcolor: NAV.surface,
  overflow: "hidden",
  boxShadow: NAV.categorySubmenuShadow,
};

const drawerListItemSx = {
  px: 2,
  py: 1.15,
  minHeight: 48,
  borderRadius: 0,
  borderBottom: `1px solid ${NAV.line}`,
  transition: NAV.transition,
  "&:last-child": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: NAV.navyMuted,
  },
};

const drawerNavTextSx = {
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: NAV.ink,
  lineHeight: 1.35,
};

const DrawerComp = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openCategorySlug, setOpenCategorySlug] = useState(null);
  const [shopCategories, setShopCategories] = useState([]);
  const authToken = useSelector((state) => state.auth.token);
  const isAuthenticated = Boolean(authToken);
  const drawerNavLinks = getDrawerNavLinks(isAuthenticated);

  const toggleCategory = (slug) => {
    setOpenCategorySlug((prev) => (prev === slug ? null : slug));
  };

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        const categories = await fetchShopCategories();
        if (isMounted) {
          setShopCategories(categories);
        }
      } catch (error) {
        if (isMounted) {
          setShopCategories([]);
        }
      }
    };

    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <React.Fragment>
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: "min(100vw - 48px, 320px)", sm: 320 },
            display: "flex",
            flexDirection: "column",
            backgroundColor: NAV.chipRowBg,
            color: NAV.ink,
            borderLeft: `1px solid ${NAV.line}`,
            boxShadow: NAV.shadowMenu,
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            py: 1.5,
            px: 1.5,
          }}
        >
          <Box
            sx={{
              ...drawerPanelSx,
              mb: 1.5,
              backgroundImage: `linear-gradient(180deg, #ffffff 0%, ${NAV.categorySubmenuBg} 100%)`,
            }}
          >
            {shopCategories.map((cat, index) => (
              <React.Fragment key={cat.slug}>
                <ListItemButton
                  {...(!cat.subcategories?.length
                    ? {
                        component: Link,
                        to: `/products?category=${encodeURIComponent(cat.slug)}`,
                        onClick: () => setOpenDrawer(false),
                      }
                    : { onClick: () => toggleCategory(cat.slug) })}
                  sx={{
                    ...drawerListItemSx,
                    borderBottom:
                      index < shopCategories.length - 1 ||
                      openCategorySlug === cat.slug
                        ? `1px solid ${NAV.line}`
                        : "none",
                    "&:hover": {
                      backgroundColor: NAV.navyMuted,
                      pl: 2.25,
                    },
                    "&:hover .MuiListItemIcon-root": {
                      color: NAV.navy,
                    },
                  }}
                >
                  <ListItemText
                    primary={cat.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: NAV.ink,
                        lineHeight: 1.35,
                      },
                    }}
                  />
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      ml: 0.5,
                      color: NAV.inkMuted,
                      transition: NAV.transition,
                    }}
                  >
                    {cat.subcategories?.length ? (
                      openCategorySlug === cat.slug ? (
                        <ExpandLessIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 20 }} />
                      )
                    ) : (
                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                </ListItemButton>

                {cat.subcategories?.length ? (
                  <Collapse
                    in={openCategorySlug === cat.slug}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      disablePadding
                      sx={{ bgcolor: NAV.categorySubmenuBg }}
                    >
                      {cat.subcategories.map((subcat, subIndex) => (
                        <ListItemButton
                          key={`${cat.slug}-${subcat.slug}`}
                          component={Link}
                          to={`/products?category=${encodeURIComponent(
                            cat.slug,
                          )}&subcategory=${encodeURIComponent(subcat.slug)}`}
                          onClick={() => setOpenDrawer(false)}
                          sx={{
                            pl: 3.25,
                            pr: 1.5,
                            py: 1,
                            minHeight: 44,
                            borderBottom:
                              subIndex < cat.subcategories.length - 1
                                ? "1px solid rgba(15, 40, 71, 0.08)"
                                : "none",
                            borderRadius: 0,
                            transition: NAV.transition,
                            "&:hover": {
                              backgroundColor: NAV.categorySubmenuRowHover,
                              boxShadow:
                                "inset 3px 0 0 0 rgba(15, 40, 71, 0.85)",
                            },
                            "&:hover .MuiTypography-root": {
                              color: NAV.navy,
                            },
                          }}
                        >
                          <ListItemText
                            primary={subcat.label}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                color: NAV.ink,
                                lineHeight: 1.35,
                              },
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                ) : null}
              </React.Fragment>
            ))}
          </Box>

          <Box sx={drawerPanelSx}>
            <List disablePadding>
              {drawerNavLinks.map((item) => (
                <ListItemButton
                  key={item.to}
                  component={Link}
                  to={item.to}
                  onClick={() => setOpenDrawer(false)}
                  sx={drawerListItemSx}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: drawerNavTextSx }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
            py: 1.5,
            px: 2,
            bgcolor: NAV.surfaceSubtle,
            borderTop: `1px solid ${NAV.line}`,
          }}
        >
          {[
            {
              icon: instagram,
              url: "https://www.instagram.com/uk_to_mars?igsh=cjViOWRuN3owcHJp&utm_source=qr",
            },
            { icon: Gmail, url: "mailto:sales@uktomars.co.uk" },
            { icon: WhatsApp, url: "http://wa.me/447564627910" },
          ].map((social, index) => (
            <IconButton
              key={index}
              onClick={() => window.open(social.url, "_blank")}
              aria-label="Social link"
              sx={{
                mx: 0.75,
                transition: NAV.transition,
                "&:hover": {
                  transform: "scale(1.08)",
                  backgroundColor: NAV.navyMuted,
                },
              }}
            >
              <img
                src={social.icon}
                alt="Social media icon"
                style={{
                  height: "24px",
                  width: "24px",
                  filter: "grayscale(30%)",
                  transition: "filter 0.3s ease",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.filter = "grayscale(0%)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.filter = "grayscale(30%)")
                }
              />
            </IconButton>
          ))}
        </Box>
      </Drawer>

      {/* Hamburger menu button */}
      <IconButton
        onClick={() => setOpenDrawer(!openDrawer)}
        aria-label="Open menu"
        sx={{
          flexShrink: 0,
          padding: "10px",
          color: "#fff",
          backgroundColor: NAV.navy,
          borderRadius: 2,
          boxShadow: `0 2px 8px ${NAV.navyMuted}`,
          transition: NAV.transition,
          "&:hover": {
            backgroundColor: "#0a1f38",
            boxShadow: `0 4px 12px ${NAV.navyHover}`,
          },
        }}
      >
        <MenuIcon fontSize="medium" />
      </IconButton>
    </React.Fragment>
  );
};

export default DrawerComp;
